/**
 * El progreso del alta, por CLIENTE, como lo cuenta la pantalla «Creando tu cartera de clientes».
 *
 * El backend (`agency/onboarding/apply-progress/`) contesta por línea: una línea es una cuenta de un
 * cliente. Pero lo que el usuario armó y lo que espera ver son sus clientes, así que acá se agrupa:
 * un cliente está listo cuando lo están todas sus líneas, y mientras tanto se muestra lo que se está
 * haciendo con la que está corriendo.
 *
 * Vive fuera del componente para poder probarlo sin montar la pantalla con su router y su contexto.
 */

export const PHASE_QUEUED = 'QUEUED';
export const PHASE_CAMPAIGNS = 'CAMPAIGNS';
export const PHASE_METRICS = 'METRICS';
export const PHASE_DASHBOARD = 'DASHBOARD';
export const PHASE_RETRYING = 'RETRYING';
export const PHASE_READY = 'READY';
export const PHASE_FAILED = 'FAILED';

export const REASON_NO_CONNECTION = 'NO_CONNECTION';

const WORKING_PHASES = [PHASE_CAMPAIGNS, PHASE_METRICS, PHASE_DASHBOARD];

// En qué orden se listan los clientes: lo que se está haciendo ahora arriba —es lo que contesta
// «¿avanza o se colgó?»—, después los que ya están listos, por nombre, y al final lo que espera.
const ORDER = {working: 0, ready: 1, retrying: 2, failed: 3, queued: 4};

/** El estado de un cliente según el de sus líneas. Una fase desconocida cuenta como en espera. */
function statusOf(lines) {
    if (lines.some(line => WORKING_PHASES.includes(line.phase))) return 'working';
    if (lines.some(line => line.phase === PHASE_RETRYING)) return 'retrying';
    if (lines.some(line => line.phase !== PHASE_READY && line.phase !== PHASE_FAILED)) return 'queued';
    if (lines.some(line => line.phase === PHASE_FAILED)) return 'failed';
    return 'ready';
}

/**
 * `[{id, name, status, phase, accountName, reason}]`, un elemento por cliente.
 *
 * `phase` y `accountName` son los de la línea que explica el estado: la que corre si hay una, o la
 * que falló. Con eso la fila dice «Trayendo las campañas · Cuenta X» y no sólo «trabajando».
 */
export function clientsFromProgress(lines) {
    const byClient = new Map();
    (lines || []).forEach(line => {
        const client = line.client || {};
        const key = client.id ?? `line-${line.business_unit_id}`;
        if (!byClient.has(key)) byClient.set(key, {id: client.id ?? null, name: client.name || line.name, lines: []});
        byClient.get(key).lines.push(line);
    });

    const clients = [];
    byClient.forEach(({id, name, lines: clientLines}) => {
        const status = statusOf(clientLines);
        const representative = clientLines.find(line => (
            status === 'working' ? WORKING_PHASES.includes(line.phase)
                : status === 'retrying' ? line.phase === PHASE_RETRYING
                    : status === 'failed' ? line.phase === PHASE_FAILED
                        : true
        )) || clientLines[0];
        clients.push({
            id, name, status,
            phase: representative.phase,
            accountName: representative.accounts?.[0]?.name || null,
            reason: representative.reason || null,
        });
    });

    return clients.sort((a, b) => (ORDER[a.status] - ORDER[b.status]) || String(a.name).localeCompare(String(b.name)));
}

export function summaryOf(clients) {
    return {
        total: clients.length,
        ready: clients.filter(client => client.status === 'ready').length,
        failed: clients.filter(client => client.status === 'failed').length,
        // Aparte de los fallidos: un reintento es transitorio, y sumarlo a «con problemas» contaría
        // como perdido algo que se va a arreglar solo. Pero se cuenta, para que toda fila ámbar tenga
        // su número arriba.
        retrying: clients.filter(client => client.status === 'retrying').length,
    };
}

// Al cerrar con problemas lo que importa es lo que falló: arriba, y no escondido al fondo del scroll
// detrás de doce «Listo».
const CLOSING_ORDER = {failed: 0, retrying: 1, queued: 2, working: 3, ready: 4};

export function clientsForClosing(clients) {
    return [...clients].sort((a, b) => (CLOSING_ORDER[a.status] - CLOSING_ORDER[b.status])
                                       || String(a.name).localeCompare(String(b.name)));
}

/** ¿Queda algún cliente que no está listo? Lo usa el tope de espera para no cerrar como éxito. */
export function hasClientsNotReady(clients) {
    return clients.some(client => client.status !== 'ready');
}

/**
 * ¿Ya no hay nada que esperar? Lo decide el backend (`poll.pending`), pero sólo sobre una respuesta
 * que trae líneas: una lista vacía puede ser que las filas recién creadas todavía no se ven.
 */
export function isSettled(progress) {
    return Boolean(progress && progress.lines && progress.lines.length && progress.poll && !progress.poll.pending);
}

/**
 * Desde cuándo está cada cliente en lo que la pantalla dice que está haciendo.
 *
 * La fila decía «Trayendo las campañas · Cuenta X» y nada más: en el alta grabada del 29/09 quedó
 * así, idéntica, de 11:35 a 11:38, y no había forma de saber si avanzaba o se había colgado (card
 * 9WXeowAo). El backend no manda cuándo empezó cada fase (contrato §4), así que se cuenta con reloj
 * LOCAL: desde la primera respuesta en que la fila apareció haciendo eso.
 *
 * «Eso» es la fase **y** la cuenta: si cambia cualquiera de las dos, la fila empezó otra cosa y el
 * reloj vuelve a cero. Devuelve `{clave del cliente → {stage, since}}` y conserva el `since` de lo
 * que no cambió; los clientes que ya no vienen se olvidan.
 *
 * Si nada cambió devuelve el MISMO mapa: va a un `setState` en cada respuesta del poll, y un mapa
 * nuevo cada vez forzaría un render por respuesta aunque la pantalla no cambie.
 */
export function trackStageSince(previous, clients, now) {
    const next = new Map();
    let changed = !previous || previous.size !== clients.length;
    clients.forEach(client => {
        const key = clientKeyOf(client);
        const stage = `${client.status}|${client.phase}|${client.accountName || ''}`;
        const before = previous && previous.get(key);
        if (before && before.stage === stage) {
            next.set(key, before);
        } else {
            changed = true;
            next.set(key, {stage, since: now});
        }
    });
    return changed ? next : previous;
}

export function clientKeyOf(client) {
    return client.id ?? client.name;
}

/**
 * Los minutos enteros que lleva un cliente trabajando en lo mismo, o `null` si no corresponde
 * mostrarlos: sólo mientras trabaja (listo, en espera o con problemas no «llevan» nada) y a partir
 * del primer minuto — «hace 0 min» no dice nada y la mayoría de las filas termina antes.
 */
export function elapsedMinutesOf(client, since, now) {
    if (client.status !== 'working' || since === null || since === undefined) return null;
    const minutes = Math.floor((now - since) / 60000);
    return minutes >= 1 ? minutes : null;
}

/** Cada cuánto volver a preguntar. El backend lo manda; si no, un default prudente. */
export function nextPollMs(progress) {
    const seconds = Number(progress?.poll?.retry_after_seconds);
    return (seconds > 0 ? seconds : 4) * 1000;
}
